import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, LifeBuoy } from 'lucide-react';
import {
  AppButton,
  AppModal,
  Callout,
  FieldGroup,
  FormField,
  Input,
  NativeSelect,
  Radio,
  Textarea,
} from '../ui';
import apiClient from '../../utils/apiClient';
import ScreenshotField, { screenshotProblem } from './ScreenshotField';
import { CATEGORIES, PRIORITIES, browserLabel, captureContext } from './supportVocabulary';

const TITLE_MAX = 200;
const FORM_ID = 'new-ticket-form';

const emptyForm = (category = '') => ({
  instituteId: '',
  category,
  title: '',
  description: '',
  priority: 'medium',
});

/**
 * NewTicketModal — file a support request.
 *
 * One form for every role. What changes is said, not built twice: a student
 * and a trainer pick from questions in their own words (`audience`), and the
 * SaaS admin — who files on an institute's behalf after a call — picks the
 * institute first (`institutes`).
 *
 * Props:
 *   show / onHide      — visibility
 *   audience           — 'student' | 'instructor' | 'staff'
 *   initialCategory    — preselects a category (a help-center topic card)
 *   institutes         — [{ _id, name }]; when passed, an institute is required
 *   ticketPath         — (id) => route of the new ticket, for "View request"
 *   onCreated          — called once the ticket exists, before the confirmation
 */
const NewTicketModal = ({
  show,
  onHide,
  audience = 'student',
  initialCategory = '',
  institutes,
  ticketPath,
  onCreated,
}) => {
  const navigate = useNavigate();
  const onBehalf = Array.isArray(institutes);
  const [form, setForm] = useState(() => emptyForm(initialCategory));
  const [screenshot, setScreenshot] = useState(null);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const fields = useRef({});
  const screenshotHintId = useId();

  useEffect(() => {
    if (!show) return;
    setForm(emptyForm(initialCategory));
    setScreenshot(null);
    setErrors({});
    setFailure('');
    setCreated(null);
  }, [show, initialCategory]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const chooseScreenshot = (file) => {
    setScreenshot(file);
    setErrors((prev) => ({ ...prev, screenshot: screenshotProblem(file) }));
  };

  const validate = () => {
    const next = {};
    if (onBehalf && !form.instituteId) next.instituteId = 'Choose the institute this request is for.';
    if (!form.category) next.category = 'Choose what this is about.';
    if (!form.title.trim()) next.title = 'Add a short subject.';
    if (!form.description.trim()) next.description = 'Describe what happened.';
    const shot = screenshotProblem(screenshot);
    if (shot) next.screenshot = shot;
    setErrors(next);
    const first = ['instituteId', 'category', 'title', 'description'].find((k) => next[k]);
    if (first) fields.current[first]?.focus();
    return !Object.values(next).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    setFailure('');

    let screenshotUrl = '';
    if (screenshot) {
      try {
        const body = new FormData();
        body.append('file', screenshot);
        const { data } = await apiClient.post('/support/screenshot-url', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        screenshotUrl = data.key || '';
      } catch {
        setErrors((prev) => ({ ...prev, screenshot: 'The screenshot didn’t upload. Try again, or remove it to send the request without it.' }));
        setSubmitting(false);
        return;
      }
    }

    try {
      const { data } = await apiClient.post('/support/report', {
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        screenshotUrl,
        ...captureContext(),
        ...(onBehalf ? { onBehalfOfInstituteId: form.instituteId } : {}),
      });
      const ticket = data.ticket || { ticketId: data.ticketId };
      setCreated(ticket);
      onCreated?.(ticket);
    } catch (err) {
      setFailure(err?.response?.data?.message || 'Your request wasn’t sent. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // A screenshot taken with the OS shortcut is on the clipboard, not on disk.
  const handlePaste = (e) => {
    const file = Array.from(e.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
    if (!file) return;
    e.preventDefault();
    chooseScreenshot(file);
  };

  const priorityHint = PRIORITIES.find((p) => p.value === form.priority)?.hint;
  const context = show ? captureContext() : null;

  if (created) {
    return (
      <AppModal show={show} onHide={onHide} size="sm" title="Request sent">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-4 tw-text-center" role="status">
          <span className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-success-soft tw-text-success">
            <CircleCheck size={28} aria-hidden="true" />
          </span>
          <div>
            <p className="tw-m-0 tw-text-[1.125rem] tw-font-semibold tw-text-content">We’ve got your request</p>
            {created.ticketId && (
              <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-content-muted">
                Its number is <span className="tw-font-mono tw-font-semibold tw-text-content">{created.ticketId}</span>.
              </p>
            )}
          </div>
          <p className="tw-m-0 tw-max-w-[22rem] tw-text-[0.8125rem] tw-text-content-muted">
            Replies appear on the request’s page, and you can add details there at any time.
          </p>
          <div className="tw-mt-2 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <AppButton variant="outline" onClick={onHide}>Done</AppButton>
            {created._id && ticketPath && (
              <AppButton onClick={() => { onHide(); navigate(ticketPath(created._id)); }}>View request</AppButton>
            )}
          </div>
        </div>
      </AppModal>
    );
  }

  return (
    <AppModal
      show={show}
      onHide={submitting ? undefined : onHide}
      closeOnOverlay={!submitting}
      size="lg"
      title={onBehalf ? 'New ticket for an institute' : 'New support request'}
      icon={<LifeBuoy size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onHide} disabled={submitting}>Cancel</AppButton>
          <AppButton type="submit" form={FORM_ID} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send request'}
          </AppButton>
        </div>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} onPaste={handlePaste} noValidate className="tw-flex tw-flex-col tw-gap-5">
        {failure && <Callout tone="danger" role="alert">{failure}</Callout>}

        {onBehalf && (
          <FormField label="Institute" required error={errors.instituteId} hint="The institute’s admins will see this ticket too.">
            <NativeSelect
              ref={(el) => { fields.current.instituteId = el; }}
              size="lg"
              value={form.instituteId}
              onChange={(e) => set('instituteId', e.target.value)}
            >
              <option value="">Choose an institute…</option>
              {institutes.map((inst) => <option key={inst._id} value={inst._id}>{inst.name}</option>)}
            </NativeSelect>
          </FormField>
        )}

        <FieldGroup legend="What is this about?">
          <div className="tw-grid tw-gap-2 min-[576px]:tw-grid-cols-2">
            {CATEGORIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <Radio
                  key={c.value}
                  ref={i === 0 ? (el) => { fields.current.category = el; } : undefined}
                  variant="card"
                  name="ticket-category"
                  value={c.value}
                  checked={form.category === c.value}
                  onChange={() => set('category', c.value)}
                  invalid={!!errors.category}
                  labelClassName="tw-font-semibold"
                  label={(
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                      <Icon size={15} aria-hidden="true" className="tw-text-primary" />
                      {audience === 'staff' ? c.label : c.topic}
                    </span>
                  )}
                  description={c.blurb[audience] || c.blurb.student}
                />
              );
            })}
          </div>
          {errors.category && (
            <p role="alert" className="tw-m-0 tw-mt-1.5 tw-text-xs tw-text-danger">{errors.category}</p>
          )}
        </FieldGroup>

        <FormField
          label="Subject"
          required
          error={errors.title}
          hint={form.title.length > TITLE_MAX - 40
            ? `${TITLE_MAX - form.title.length} characters left`
            : 'One line, like “The recording stops after 2 minutes”.'}
        >
          <Input
            ref={(el) => { fields.current.title = el; }}
            size="lg"
            value={form.title}
            maxLength={TITLE_MAX}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Summarise the problem or question"
          />
        </FormField>

        <FormField
          label="Details"
          required
          error={errors.description}
          hint="What happened, what you expected instead, and the steps to see it again."
        >
          <Textarea
            ref={(el) => { fields.current.description = el; }}
            rows={6}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="The more we know, the faster we can help."
          />
        </FormField>

        <div className="tw-grid tw-gap-5 min-[768px]:tw-grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
          <FormField label="Priority" hint={priorityHint}>
            <NativeSelect size="lg" value={form.priority} onChange={(e) => set('priority', e.target.value)} options={PRIORITIES} />
          </FormField>
          <FieldGroup legend="Screenshot (optional)">
            <ScreenshotField file={screenshot} onChange={chooseScreenshot} describedBy={screenshotHintId} />
            <p id={screenshotHintId} className={`tw-m-0 tw-mt-1.5 tw-text-xs ${errors.screenshot ? 'tw-text-danger' : 'tw-text-content-subtle'}`} role={errors.screenshot ? 'alert' : undefined}>
              {errors.screenshot || 'PNG, JPG, WebP or GIF, up to 10 MB.'}
            </p>
          </FieldGroup>
        </div>

        {context && (
          <details className="tw-rounded-md tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-xs tw-text-content-muted">
            <summary className="tw-cursor-pointer tw-select-none tw-font-medium tw-text-content-muted tw-focus-ring">
              We’ll include the page you’re on and your browser, to help us reproduce it
            </summary>
            <dl className="tw-m-0 tw-mt-2 tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-gap-x-3 tw-gap-y-1">
              <dt className="tw-text-content-subtle">Page</dt>
              <dd className="tw-m-0 tw-break-all tw-font-mono tw-text-content">{context.pageUrl}</dd>
              <dt className="tw-text-content-subtle">Browser</dt>
              <dd className="tw-m-0 tw-text-content">{browserLabel(context.browser) || '—'}</dd>
              <dt className="tw-text-content-subtle">Screen</dt>
              <dd className="tw-m-0 tw-text-content">{context.screenResolution}</dd>
            </dl>
          </details>
        )}
      </form>
    </AppModal>
  );
};

export default NewTicketModal;
